/**
 * `/billing/*` on qv-data: what this account may buy, and the two hand-offs to
 * Stripe (Checkout to buy, the portal to cancel or change a card).
 *
 * **Reads degrade, writes throw** — `api/journal.ts`'s split. A plan list that
 * fails leaves the plan screen without its top section, which is chrome; a
 * checkout that failed quietly leaves somebody clicking a button that does
 * nothing.
 *
 * Nothing is granted by anything here. The grant is the webhook's, once Stripe
 * says paid — so after a checkout the screen polls `plans()` until `current`
 * appears, rather than assuming.
 */

import type { BillingPlans, Interval, PauseStart } from "../lib/billing";
import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

function url(path: string): string {
  return `${BASE}${path}?userId=${encodeURIComponent(currentUserId())}`;
}

export function billingConfigured(): boolean {
  return BASE !== "";
}

export async function plans(): Promise<BillingPlans | null> {
  if (!BASE) return null;
  try {
    const response = await fetch(url("/billing/plans"), { headers: await requestHeaders() });
    if (!response.ok) return null;
    return (await response.json()) as BillingPlans;
  } catch {
    return null;
  }
}

async function redirectTo(path: string, body?: unknown): Promise<string> {
  if (!BASE) throw new Error("no billing server configured");
  const response = await fetch(url(path), {
    method: "POST",
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(detail?.detail ?? `billing ${response.status}`);
  }
  const { url: target } = (await response.json()) as { url: string };
  return target;
}

/** A Stripe Checkout URL for this plan. The caller navigates to it. */
export function checkout(plan: string, interval: Interval): Promise<string> {
  return redirectTo("/billing/checkout", { plan, interval });
}

/** A Checkout URL for the one-time upgrade to `plan`; the renewal date stays. */
export function upgrade(plan: string): Promise<string> {
  return redirectTo("/billing/upgrade", { plan });
}

/** A Stripe customer-portal URL. The caller navigates to it. */
export function portal(): Promise<string> {
  return redirectTo("/billing/portal");
}

async function post(path: string, body?: unknown): Promise<BillingPlans> {
  if (!BASE) throw new Error("no billing server configured");
  const response = await fetch(url(path), {
    method: "POST",
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(detail?.detail ?? `billing ${response.status}`);
  }
  return (await response.json()) as BillingPlans;
}

/** Pause the plan for all the days left this year. Answers as `plans()`. */
export function pausePlan(start: PauseStart): Promise<BillingPlans> {
  return post("/billing/pause", { start });
}

/** Call off a pause that has not started. */
export function cancelPause(): Promise<BillingPlans> {
  return post("/billing/pause/cancel");
}

/** End an active pause now. */
export function resumePlan(): Promise<BillingPlans> {
  return post("/billing/pause/resume");
}
